import { TicketConfirmState } from 'components/ChatWidget/typing';
import ActionConstants from '../constants/';

interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  type?: string;
  current_step?: string;
}

interface ChatbotState {
  isOpen: boolean;
  isLoading: boolean;
  messages: ChatMessage[];
  suggestions: string[];
  error: string | null;
  /** Whether an older page of history exists to load */
  hasMoreHistory: boolean;
  /** True while a "load more" (scroll-back) request is in flight */
  isLoadingMoreHistory: boolean;
  /** Total messages in the session (from the first page); used to show "X of Y loaded" */
  totalHistoryCount: number;
  /** Field label set by AI helper click — sent as context with next user message */
  aiHelperFieldLabel: string | null;
  /** Error context gathered from DOM when tooltip is shown due to form/API errors — sent invisibly to bot */
  verificationErrorContext: { errors: string[]; route: string } | null;
  ticketConfirm: {
    visible: boolean;
    summary: string;
    merchantId: string;
    title: string;
    description: string;
    category: string;
    issueType: string;
    processStep: string;
    priority: string;
  } | null;
  ticketCreating: boolean;
  ticketError: string | null;
  ticketState: 'open' | 'closed' | 'processing' | 'error' | null;
  ticketCount: number;
  /** Last created ticket details */
  lastTicket: {
    ticketId: string;
    ticketNumber: string | null;
    status: string;
    priority: string;
    createdAt: string;
    providerSyncStatus: string;
  } | null;
  /** Whether the ticket list panel is shown */
  showTicketList: boolean;
  /** Tickets from the Ticketing Service */
  tickets: Array<{
    ticketId: string;
    ticketNumber: string | null;
    title: string;
    description: string;
    status: string;
    priority: string;
    createdAt: string;
    processStep?: string;
    ownerTeam?: string;
  }>;
}

interface HistoryItem {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  type?: string;
  current_step?: string;
}

const initialState: ChatbotState = {
  isOpen: false,
  isLoading: false,
  messages: [],
  suggestions: [],
  error: null,
  hasMoreHistory: false,
  isLoadingMoreHistory: false,
  totalHistoryCount: 0,
  /** Field label set by AI helper click — sent as context with next user message */
  aiHelperFieldLabel: null,
  /** Error context gathered from DOM / API errors on verification screens */
  verificationErrorContext: null,
  ticketConfirm: {
    visible: false,
    summary: '',
    merchantId: '',
    title: '',
    description: '',
    category: 'other',
    issueType: 'OTHER',
    processStep: '',
    priority: 'medium',
  },
  ticketCreating: false,
  ticketError: null,
  ticketState: 'open',
  ticketCount: 0,
  lastTicket: null,
  showTicketList: false,
  tickets: [],
};

const chatbotReducer = (
  state = initialState,
  action: { type: string; payload?: any },
): ChatbotState => {
  switch (action.type) {
    case ActionConstants.TOGGLE_CHAT_WIDGET:
      return { ...state, isOpen: !state.isOpen };

    case ActionConstants.SET_AI_HELPER_CONTEXT:
      return { ...state, aiHelperFieldLabel: action.payload.fieldLabel };

    case ActionConstants.CLEAR_AI_HELPER_CONTEXT:
      return { ...state, aiHelperFieldLabel: null };

    case ActionConstants.SET_VERIFICATION_ERROR_CONTEXT:
      return { ...state, verificationErrorContext: action.payload };

    case ActionConstants.CLEAR_VERIFICATION_ERROR_CONTEXT:
      return { ...state, verificationErrorContext: null };

    case ActionConstants.ADD_LOCAL_CHAT_MESSAGE:
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            role: action.payload.role,
            content: action.payload.content,
            timestamp: Date.now(),
            type: action.payload.type,
            current_step: action.payload.current_step,
          },
        ],
      };

    case ActionConstants.SEND_CHAT_MESSAGE_REQUEST:
      return { ...state, isLoading: true, error: null };

    case ActionConstants.SEND_CHAT_MESSAGE_SUCCESS: {
      const { body } = action.payload;
      const answer = body?.answer || 'Sorry, I could not process your request.';
      return {
        ...state,
        isLoading: false,
        messages: [
          ...state.messages,
          {
            role: 'assistant',
            content: answer,
            timestamp: Date.now(),
            type: action.payload.type,
            current_step: action.payload.current_step,
          },
        ],
      };
    }

    case ActionConstants.SEND_CHAT_MESSAGE_FAILURE: {
      return {
        ...state,
        isLoading: false,
        error: 'Something went wrong',
        messages: [
          ...state.messages,
          {
            role: 'assistant',
            content: 'Sorry, something went wrong. Please try again in a moment.',
            timestamp: Date.now(),
            type: action.payload.type,
            current_step: action.payload.current_step,
          },
        ],
      };
    }

    // ---- SSE streaming cases ----
    case ActionConstants.CHAT_STREAM_START:
      // Create an empty assistant message that chunks will append into.
      return {
        ...state,
        isLoading: true,
        error: null,
        messages: [
          ...state.messages,
          {
            role: 'assistant',
            content: '',
            timestamp: Date.now(),
            type: 'message',
            current_step: action.payload?.current_step || '',
          },
        ],
      };

    case ActionConstants.CHAT_STREAM_CHUNK: {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === 'assistant') {
        messages[messages.length - 1] = {
          ...last,
          content: last.content + (action.payload?.content || ''),
        };
      }
      return { ...state, messages };
    }

    case ActionConstants.CHAT_STREAM_END:
      return { ...state, isLoading: false };

    case ActionConstants.CHAT_STREAM_ERROR: {
      const content = "I couldn't get a response. Please try again later.";
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === 'assistant' && last.content === '') {
        messages[messages.length - 1] = {
          ...last,
          content,
          timestamp: Date.now(),
          type: 'error',
          current_step: action.payload.current_step,
        };
      } else {
        messages.push({ role: 'assistant', content, timestamp: Date.now(), type: 'error', current_step: action.payload.current_step });
      }
      return { ...state, isLoading: false, error: 'Something went wrong', messages };
    }

    case ActionConstants.CLEAR_CHAT_HISTORY_SUCCESS:
      return { ...state, messages: [] };

    case ActionConstants.GET_CHAT_SUGGESTIONS_SUCCESS: {
      const suggestions = action.payload?.body?.suggestions || [];
      return { ...state, suggestions: suggestions };
    }

    case ActionConstants.RESET_CHAT_STATE:
      return { ...initialState };

    case ActionConstants.CHAT_TICKET_CONFIRM: {
      return {
        ...state,
        ticketConfirm: {
          visible: true,
          summary: action.payload?.summary || '',
          merchantId: action.payload?.merchantId || '',
          title: action.payload?.title || '',
          description: action.payload?.description || '',
          category: action.payload?.category || '',
          issueType: action.payload?.issueType || 'OTHER',
          processStep: action.payload?.processStep || '',
          priority: action.payload?.priority || '',
        },
      };
    }

    case ActionConstants.CHAT_TICKET_CONFIRM_DISMISS:
      return { ...state, ticketConfirm: null };

    case ActionConstants.CHAT_TICKET_CONFIRM_CANCEL:
      return { ...state, ticketConfirm: { ...state.ticketConfirm, visible: true } as TicketConfirmState };

    case ActionConstants.CHAT_TICKET_CREATING:
      return { ...state, ticketCreating: true };

    case ActionConstants.CHAT_TICKET_CREATED: {
      const body = action.payload?.body || {};
      const ticketId = body?.ticketId || '';
      const ticketNumber = body?.ticketNumber || null;
      const status = body?.status || 'OPEN';
      const priority = body?.priority || 'P3';
      const createdAt = body?.createdAt || new Date().toISOString();
      const providerSyncStatus = body?.providerSyncStatus || 'SYNCED';
      const lastTicket = { ticketId, ticketNumber, status, priority, createdAt, providerSyncStatus };

      // Push the raw API response as JSON so TicketCreationSuccess can render it,
      // matching the format of SSE-stream ticket_created messages.
      const ticketContent = JSON.stringify(body);

      const ticketMessages = [...state.messages];
      ticketMessages.push({
        role: 'assistant',
        content: ticketContent,
        timestamp: Date.now(),
        type: 'ticket_created',
        current_step: action.payload.current_step,
        fromLocal: true,
      } as any);
      return {
        ...state,
        ticketCreating: false,
        ticketConfirm: null,
        messages: ticketMessages,
        ticketState: 'closed',
        ticketCount: (state.ticketCount || 0) + 1,
        lastTicket,
        tickets: [
          {
            ticketId: lastTicket.ticketId,
            ticketNumber: lastTicket.ticketNumber,
            title: action.payload?.extra?.title || '',
            description: action.payload?.extra?.description || '',
            status: lastTicket.status,
            priority: lastTicket.priority,
            createdAt: lastTicket.createdAt,
            processStep: action.payload?.extra?.processStep || body?.processStep || '',
          },
          ...(state.tickets || []),
        ],
      };
    }

    case ActionConstants.CHAT_TICKET_CREATE_FAILED:
      return { ...state, ticketCreating: false, ticketError: action.payload?.error || 'Failed to create ticket' };

    case 'INIT_CHATBOT_SUCCESS': {
      const body = action.payload?.body || {};
      const ticketCount = body?.ticketCount ?? 0;
      const ticketList = body?.tickets || [];
      const historyItems: HistoryItem[] = body?.history || [];
      const msgs = historyItems.map((h: HistoryItem) => ({
        id: h.id,
        role: h.role,
        content: h.content,
        timestamp: Date.now(),
        type: h.type,
        current_step: h.current_step,
      }));
      return {
        ...state,
        ticketCount,
        tickets: ticketList,
        messages: msgs,
        hasMoreHistory: !!body?.hasMore,
        totalHistoryCount: body?.totalCount ?? 0,
      };
    }

    case ActionConstants.LOAD_MORE_CHAT_HISTORY_REQUEST:
      return { ...state, isLoadingMoreHistory: true };

    case ActionConstants.LOAD_MORE_CHAT_HISTORY_SUCCESS: {
      const body = action.payload?.body || {};
      const olderItems: HistoryItem[] = body?.items || [];
      const olderMsgs = olderItems.map((h: HistoryItem) => ({
        id: h.id,
        role: h.role,
        content: h.content,
        timestamp: Date.now(),
        type: h.type,
        current_step: h.current_step,
      }));
      return {
        ...state,
        isLoadingMoreHistory: false,
        messages: [...olderMsgs, ...state.messages],
        hasMoreHistory: !!body?.hasMore,
        // "load more" pages don't recompute the total — keep what we got on init
        totalHistoryCount: typeof body?.totalCount === 'number' ? body.totalCount : state.totalHistoryCount,
      };
    }

    case ActionConstants.LOAD_MORE_CHAT_HISTORY_FAILURE:
      return { ...state, isLoadingMoreHistory: false };

    case ActionConstants.CHAT_TOGGLE_TICKET_LIST:
      return { ...state, showTicketList: !state.showTicketList };

    case ActionConstants.CHAT_TICKET_LIST_SUCCESS: {
      const body = action.payload?.body || {};
      const ticketList = body?.data || body?.tickets || [];
      return { ...state, tickets: ticketList };
    }

    case ActionConstants.CHAT_TICKET_LIST_FAILURE:
      return state; // silently ignore — tickets remain as-is

    default:
      return state;
  }
};

export default chatbotReducer;
